import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CommentsWidget,
  CommentsWidgetCreatedEnvelope,
  CommentsWidgetDeletedEnvelope,
  CommentsWidgetQuery,
  CommentsWidgetUpdatedEnvelope,
  CommentsWidgetsQueryBuilder,
  CursorQuery,
  DeleteCommentsWidgetOptions,
  QueryCommentsWidgetResourcesResponse,
  QueryCommentsWidgetResponse,
  RegisterCommentsWidgetOptions,
  RegisterCommentsWidgetResponse,
  UpdateCommentsWidget,
  createCommentsWidget as universalCreateCommentsWidget,
  deleteCommentsWidget as universalDeleteCommentsWidget,
  getCommentsWidget as universalGetCommentsWidget,
  queryCommentsWidget as universalQueryCommentsWidget,
  queryCommentsWidgetResources as universalQueryCommentsWidgetResources,
  registerCommentsWidget as universalRegisterCommentsWidget,
  typedQueryCommentsWidget as universalTypedQueryCommentsWidget,
  updateCommentsWidget as universalUpdateCommentsWidget,
} from './comments-categories-v1-comments-widget-widgets.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/comments' };

export function registerCommentsWidget(
  httpClient: HttpClient
): RegisterCommentsWidgetSignature {
  return (
    categoryId: string,
    options?: NonNullablePaths<RegisterCommentsWidgetOptions, `resourceId`, 2>
  ) =>
    universalRegisterCommentsWidget(
      categoryId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RegisterCommentsWidgetSignature {
  /**
   * Registers CommentsWidget. Whenever a widget is loaded for the first time or its resourceId changes, it saves the new resource information about the widget
   * @param - Widget category ID
   */
  (
    categoryId: string,
    options?: NonNullablePaths<RegisterCommentsWidgetOptions, `resourceId`, 2>
  ): Promise<
    NonNullablePaths<
      RegisterCommentsWidgetResponse,
      `commentsWidget.categoryId` | `commentsWidget.resourceId`,
      3
    >
  >;
}

export function queryCommentsWidgetResources(
  httpClient: HttpClient
): QueryCommentsWidgetResourcesSignature {
  return (query: CursorQuery) =>
    universalQueryCommentsWidgetResources(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCommentsWidgetResourcesSignature {
  /**
   * Returns all registered widget resource ids contained in metaSite
   * @param - Cursor query
   */
  (query: CursorQuery): Promise<
    NonNullablePaths<QueryCommentsWidgetResourcesResponse, `resourceIds`, 2>
  >;
}

export function createCommentsWidget(
  httpClient: HttpClient
): CreateCommentsWidgetSignature {
  return (
    commentsWidget: NonNullablePaths<
      CommentsWidget,
      `categoryId` | `resourceId`,
      2
    >
  ) =>
    universalCreateCommentsWidget(
      commentsWidget,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCommentsWidgetSignature {
  /**
   * Creates a new CommentsWidget
   * @param - CommentsWidget to be created
   * @returns The created CommentsWidget
   */
  (
    commentsWidget: NonNullablePaths<
      CommentsWidget,
      `categoryId` | `resourceId`,
      2
    >
  ): Promise<NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>>;
}

export function getCommentsWidget(
  httpClient: HttpClient
): GetCommentsWidgetSignature {
  return (commentsWidgetId: string) =>
    universalGetCommentsWidget(
      commentsWidgetId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCommentsWidgetSignature {
  /**
   * Get a CommentsWidget by id
   * @param - Id of the CommentsWidget to retrieve
   * @returns The retrieved CommentsWidget
   */
  (commentsWidgetId: string): Promise<
    NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>
  >;
}

export function updateCommentsWidget(
  httpClient: HttpClient
): UpdateCommentsWidgetSignature {
  return (
    _id: string,
    commentsWidget: NonNullablePaths<UpdateCommentsWidget, `revision`, 2>
  ) =>
    universalUpdateCommentsWidget(
      _id,
      commentsWidget,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCommentsWidgetSignature {
  /**
   * Update CommentsWidget, supports partial update
   * Pass the latest `revision` for a successful update
   * @param - CommentsWidget ID
   * @returns The updated CommentsWidget
   */
  (
    _id: string,
    commentsWidget: NonNullablePaths<UpdateCommentsWidget, `revision`, 2>
  ): Promise<NonNullablePaths<CommentsWidget, `categoryId` | `resourceId`, 2>>;
}

export function deleteCommentsWidget(
  httpClient: HttpClient
): DeleteCommentsWidgetSignature {
  return (commentsWidgetId: string, options?: DeleteCommentsWidgetOptions) =>
    universalDeleteCommentsWidget(
      commentsWidgetId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCommentsWidgetSignature {
  /**
   * Delete a CommentsWidget
   * @param - Id of the CommentsWidget to delete
   */
  (
    commentsWidgetId: string,
    options?: DeleteCommentsWidgetOptions
  ): Promise<void>;
}

export function queryCommentsWidget(
  httpClient: HttpClient
): QueryCommentsWidgetSignature {
  return () =>
    universalQueryCommentsWidget(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCommentsWidgetSignature {
  /**
   * Query CommentsWidgets using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   */
  (): CommentsWidgetsQueryBuilder;
}

export function typedQueryCommentsWidget(
  httpClient: HttpClient
): TypedQueryCommentsWidgetSignature {
  return (query: CommentsWidgetQuery) =>
    universalTypedQueryCommentsWidget(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCommentsWidgetSignature {
  /** */
  (query: CommentsWidgetQuery): Promise<
    NonNullablePaths<
      QueryCommentsWidgetResponse,
      | `commentsWidgets`
      | `commentsWidgets.${number}.categoryId`
      | `commentsWidgets.${number}.resourceId`,
      4
    >
  >;
}

export const onCommentsWidgetCreated = EventDefinition(
  'wix.comments.categories.v1.comments_widget_created',
  true,
  (event: CommentsWidgetCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.urlPath' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CommentsWidgetCreatedEnvelope>();
export const onCommentsWidgetDeleted = EventDefinition(
  'wix.comments.categories.v1.comments_widget_deleted',
  true,
  (event: CommentsWidgetDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'undefined.urlPath' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CommentsWidgetDeletedEnvelope>();
export const onCommentsWidgetUpdated = EventDefinition(
  'wix.comments.categories.v1.comments_widget_updated',
  true,
  (event: CommentsWidgetUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.urlPath' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CommentsWidgetUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CommentsWidget,
  CommentsWidgetCreatedEnvelope,
  CommentsWidgetDeletedEnvelope,
  CommentsWidgetQuerySpec,
  CommentsWidgetUpdatedEnvelope,
  CommentsWidgetsQueryBuilder,
  CommentsWidgetsQueryResult,
  CreateCommentsWidgetRequest,
  CreateCommentsWidgetResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteCommentsWidgetOptions,
  DeleteCommentsWidgetRequest,
  DeleteCommentsWidgetResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetCommentsWidgetRequest,
  GetCommentsWidgetResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Paging,
  PagingMetadataV2,
  QueryCommentsWidgetRequest,
  QueryCommentsWidgetResourcesRequest,
  QueryCommentsWidgetResourcesResponse,
  QueryCommentsWidgetResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RegisterCommentsWidgetOptions,
  RegisterCommentsWidgetRequest,
  RegisterCommentsWidgetResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UpdateCommentsWidget,
  UpdateCommentsWidgetRequest,
  UpdateCommentsWidgetResponse,
  WebhookIdentityType,
  utils,
} from './comments-categories-v1-comments-widget-widgets.universal.js';
